import React, { useState, useEffect } from 'react';

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  const navItems = [
    { name: 'Home', href: '#hero', id: 'home' },
    { name: 'Services', href: '#services', id: 'services' },
    { name: 'Testimonials', href: '#testimonials', id: 'testimonials' }
  ];

  // Scroll effect for navigation
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      setScrolled(isScrolled);
      
      // Update active section based on scroll position
      const sections = navItems.map(item => document.querySelector(item.href));
      const scrollPosition = window.scrollY + 100;

      sections.forEach((section, index) => {
        if (section) {
          const sectionTop = section.offsetTop;
          const sectionBottom = sectionTop + section.offsetHeight;
          
          if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
            setActiveSection(navItems[index].id);
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    // Trigger immediately on load
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href, id) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setActiveSection(id);
    setIsMenuOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <>
      <style>
        {`
          .navigation {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            z-index: 1000;
            transition: all 0.3s ease;
            background: transparent;
            backdrop-filter: blur(0px);
            font-family: 'Comfortaa', cursive;
            padding: 0.5rem 0;
            opacity: 1;
            visibility: visible;
            animation: fadeInDown 0.5s ease-out;
          }

          @keyframes fadeInDown {
            from {
              opacity: 1;
              transform: translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .navigation.scrolled {
            background: rgba(10, 15, 28, 0.98);
            backdrop-filter: blur(20px);
            border-bottom: 1px solid rgba(243, 179, 61, 0.1);
            padding: 0.3rem 0;
          }

          .nav-container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 0 2rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          /* Logo */
          .nav-logo {
            font-family: 'Cormorant Garamond', serif;
            font-size: 1.8rem;
            font-weight: 600;
            background: linear-gradient(45deg, #f3b33d, #f6d69b);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            text-decoration: none;
            transition: all 0.3s ease;
          }

          .nav-logo:hover {
            transform: scale(1.05);
          }

          /* Desktop Navigation */
          .nav-menu {
            display: flex;
            list-style: none;
            gap: 2rem;
            margin: 0;
            padding: 0;
            align-items: center;
          }

          .nav-item {
            position: relative;
          }

          .nav-link {
            color: #9aa7b2;
            text-decoration: none;
            font-size: 0.9rem;
            font-weight: 500;
            padding: 0.3rem 0;
            transition: all 0.3s ease;
            position: relative;
            background: none;
            border: none;
            cursor: pointer;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            font-family: 'Comfortaa', cursive;
          }

          .nav-link:hover {
            color: #ffffff;
          }

          .nav-link.active {
            color: #f3b33d;
          }

          .nav-link.active::after {
            content: '';
            position: absolute;
            bottom: -2px;
            left: 0;
            width: 100%;
            height: 1px;
            background: #f3b33d;
            border-radius: 1px;
            animation: underlineGrow 0.3s ease-out;
          }

          @keyframes underlineGrow {
            from {
              width: 0;
            }
            to {
              width: 100%;
            }
          }

          /* Contact Button */
          .nav-contact-btn {
            background: transparent;
            color: #f3b33d;
            border: 1px solid #f3b33d;
            padding: 8px 20px;
            border-radius: 8px;
            font-weight: 500;
            font-size: 0.85rem;
            cursor: pointer;
            transition: all 0.3s ease;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-left: 1rem;
            font-family: 'Comfortaa', cursive;
          }

          .nav-contact-btn:hover {
            background: rgba(243, 179, 61, 0.1);
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(243, 179, 61, 0.2);
          }

          /* Mobile Menu Button */
          .mobile-menu-btn {
            display: none;
            flex-direction: column;
            justify-content: space-between;
            width: 24px;
            height: 18px;
            background: transparent;
            border: none;
            cursor: pointer;
            padding: 0;
          }

          .menu-line {
            width: 100%;
            height: 2px;
            background: #f3b33d;
            border-radius: 1px;
            transition: all 0.3s ease;
            transform-origin: left;
          }

          .mobile-menu-btn.open .menu-line:nth-child(1) {
            transform: rotate(45deg) scaleX(1.1);
          }

          .mobile-menu-btn.open .menu-line:nth-child(2) {
            opacity: 0;
            transform: scaleX(0);
          }

          .mobile-menu-btn.open .menu-line:nth-child(3) {
            transform: rotate(-45deg) scaleX(1.1);
          }

          /* Mobile Navigation */
          .mobile-nav {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100vh;
            background: rgba(10, 15, 28, 0.98);
            backdrop-filter: blur(30px);
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            z-index: 999;
            opacity: 0;
            visibility: hidden;
            transition: all 0.3s ease;
          }

          .mobile-nav.open {
            opacity: 1;
            visibility: visible;
          }

          .mobile-nav-menu {
            list-style: none;
            padding: 0;
            margin: 0;
            text-align: center;
          }

          .mobile-nav-item {
            margin: 1.2rem 0;
            animation: fadeInUp 0.5s ease-out;
            animation-fill-mode: both;
            opacity: 1;
            visibility: visible;
          }

          @keyframes fadeInUp {
            from {
              opacity: 1;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .mobile-nav-item:nth-child(1) { animation-delay: 0.1s; }
          .mobile-nav-item:nth-child(2) { animation-delay: 0.2s; }
          .mobile-nav-item:nth-child(3) { animation-delay: 0.3s; }
          .mobile-nav-item:nth-child(4) { animation-delay: 0.4s; }

          .mobile-nav-link {
            color: #9aa7b2;
            text-decoration: none;
            font-size: 1.4rem;
            font-weight: 500;
            transition: all 0.3s ease;
            background: none;
            border: none;
            cursor: pointer;
            text-transform: uppercase;
            letter-spacing: 1px;
            font-family: 'Comfortaa', cursive;
          }

          .mobile-nav-link:hover,
          .mobile-nav-link.active {
            color: #f3b33d;
            transform: scale(1.05);
          }

          /* Mobile Close Button */
          .mobile-nav-close {
            position: absolute;
            top: 1.5rem;
            right: 1.5rem;
            background: none;
            border: none;
            color: #f3b33d;
            font-size: 1.5rem;
            cursor: pointer;
            padding: 0.5rem;
            transition: all 0.3s ease;
          }

          .mobile-nav-close:hover {
            transform: rotate(90deg);
          }

          /* Mobile Contact Button */
          .mobile-contact-btn {
            background: transparent;
            color: #f3b33d;
            border: 1px solid #f3b33d;
            padding: 12px 30px;
            border-radius: 8px;
            font-weight: 500;
            font-size: 1rem;
            cursor: pointer;
            transition: all 0.3s ease;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-top: 2rem;
            font-family: 'Comfortaa', cursive;
            animation: fadeInUp 0.5s ease-out 0.5s both;
          }

          .mobile-contact-btn:hover {
            background: rgba(243, 179, 61, 0.1);
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(243, 179, 61, 0.2);
          }

          /* Responsive Design */
          @media (max-width: 768px) {
            .nav-menu {
              display: none;
            }

            .mobile-menu-btn {
              display: flex;
            }

            .nav-container {
              padding: 0 1.5rem;
            }

            .nav-logo {
              font-size: 1.6rem;
            }
          }

          @media (max-width: 480px) {
            .nav-container {
              padding: 0 1rem;
            }

            .nav-logo {
              font-size: 1.4rem;
            }

            .mobile-nav-link {
              font-size: 1.2rem;
            }
            
            .mobile-contact-btn {
              padding: 10px 24px;
              font-size: 0.9rem;
            }
          }
        `}
      </style>

      <nav className={`navigation ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          {/* Logo */}
          <a 
            href="#hero" 
            className="nav-logo"
            onClick={(e) => {
              e.preventDefault();
              scrollToSection('#hero', 'home');
            }}
          >
            Ermiyas
          </a>

          {/* Desktop Navigation */}
          <ul className="nav-menu">
            {navItems.map((item) => (
              <li key={item.name} className="nav-item">
                <button
                  className={`nav-link ${activeSection === item.id ? 'active' : ''}`}
                  onClick={() => scrollToSection(item.href, item.id)}
                >
                  {item.name}
                </button>
              </li>
            ))}
            <li>
              <button 
                className="nav-contact-btn"
                onClick={() => scrollToSection('#contact', 'contact')}
              >
                Get In Touch
              </button>
            </li>
          </ul>

          {/* Mobile Menu Button */}
          <button 
            className={`mobile-menu-btn ${isMenuOpen ? 'open' : ''}`}
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            <span className="menu-line"></span>
            <span className="menu-line"></span>
            <span className="menu-line"></span>
          </button>

          {/* Mobile Navigation */}
          <div className={`mobile-nav ${isMenuOpen ? 'open' : ''}`}>
            <button 
              className="mobile-nav-close"
              onClick={toggleMenu}
              aria-label="Close menu"
            >
              ✕
            </button>
            
            <ul className="mobile-nav-menu">
              {navItems.map((item) => (
                <li key={item.name} className="mobile-nav-item">
                  <button
                    className={`mobile-nav-link ${activeSection === item.id ? 'active' : ''}`}
                    onClick={() => scrollToSection(item.href, item.id)}
                  >
                    {item.name}
                  </button>
                </li>
              ))}
              
              <li className="mobile-nav-item">
                <button 
                  className="mobile-contact-btn"
                  onClick={() => scrollToSection('#contact', 'contact')}
                >
                  Get In Touch
                </button>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navigation;